import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {DocumentNode} from 'graphql';
import React from 'react';

import {trackEvent} from 'analytics/Analytics';
import {ACTION_CATEGORY, ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {
  InventoryFeature,
  LabAutomationFeature,
  MolbioFeature,
  ProcessesFeature,
  RegistryFeature,
  ResultsFeature,
} from 'auth/capabilities/CapabilityFeatureConstants';
import {areAnyFeaturesEnabled, isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import * as PolicyDAO from 'auth/PolicyDAO';
import {signOut} from 'auth/signOut';
import * as BenchlingAppDAO from 'benchling-apps/BenchlingAppDAO';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import * as ChemistryDAO from 'chemistry/ChemistryDAO';
import ConfigMigrationDAO from 'config-migration/ConfigMigrationDAO';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {User} from 'constants/UserConstants';
import * as EventsDAO from 'events/EventsDAO';
import ExportModal from 'exports/ExportModal';
import {ErrorHandlingQuery} from 'graphql-client/BenchlingQuery';
import HomeDashboardActions from 'home-dashboard/HomeDashboardActions';
import {getTenantLevelInventorySettingsURL} from 'inventory/InventoryConstants';
import {AA_MONOMER_LIBRARY_URL} from 'molbio/aa-monomers/AaMonomerConstants';
import {areAaMonomersEnabledForUser} from 'molbio/aa-monomers/AaMonomersUtil';
import * as MonomersDAO from 'molbio/monomers/MonomersDAO';
import {areMonomersEnabledForUser} from 'molbio/monomers/MonomersUtil';
import {AllSourcesQuery} from 'nav/graphql/__generated/AllSourcesQuery';
import {ALL_SOURCES_QUERY} from 'nav/graphql/AllSources';
import {UserMenuQuery} from 'nav/navbar/__generated/UserMenuQuery';
import {ZENDESK_GUIDE_URL} from 'nav/ZendeskConstants';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import * as ReviewLifecyclesDAO from 'notebook/reviews/review-lifecycles/ReviewLifecyclesDAO';
import {areReviewLifecyclesEnabledForUser} from 'notebook/reviews/review-lifecycles/ReviewLifecyclesUtil';
import {Organization, Team} from 'orgs/OrganizationConstants';
import OrganizationDAO from 'orgs/OrganizationDAO';
import * as TeamDAO from 'orgs/TeamDAO';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import * as ParameterLibraryDAO from 'procedures/parameter-library/network/LibraryParameterDefinitionDAO';
import * as ProcessSchemaDAO from 'processes/dao/ProcessSchemaDAO';
import * as RegistryUtil from 'registry/RegistryUtil';
import {getLegacyRequestObjectName} from 'requests/RequestConstants';
import {ASSAY_TYPES} from 'results/assay-schemas/AssaySchemaConstants';
import * as AssaySchemaDAO from 'results/assay-schemas/AssaySchemaDAO';
import {RESULT_PRODUCT_SCHEMAS} from 'results/ResultsNamingConstants';
import * as TestDefinitionDAO from 'sample-tests/network/TestDefinitionDAO';
import {SAMPLE_TYPES_URL} from 'sample-types/network/SampleTypeDAO';
import {schemaInterfacesURL} from 'schemas/schema-interface/SchemaInterfaceDAO';
import {schemaFieldsetNamePluralized} from 'schemas/schema-interface/util/schemaFieldsetName';
import {shouldShowDeveloperConsole} from 'sidebar/DeveloperConsoleSettings';
import UpgradeAccountModal from 'start/upgrade-account/UpgradeAccountModal';
import {getStudySchemaListingURL} from 'studies/study-schemas/StudySchemaDAO';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {PADDING_SIDEPANEL} from 'styles/StyleConstants';
import {ellipsisText} from 'styles/Utilities';
import getTestProps from 'test/lib/cypress/getTestProps';
import Avatar, {NO_AVATAR_URL} from 'ui-library/avatar/Avatar';
import {AvatarShape, AvatarSize} from 'ui-library/avatar/AvatarConstants';
import * as Colors from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth, OptionContentType} from 'ui-library/menus/MenuConstants';
import Option, {OptionProps} from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import * as UnitDictionaryDAO from 'units/UnitDictionaryDAO';
import {capitalize} from 'util/NameUtil';
import {BasicEvent} from 'util/Types';

import {NavStoreSourceGraphQL} from '../../../graphql-fragments/nav/__generated/NavStoreSourceGraphQL';
import AboutBenchlingModal, {isAboutBenchlingModalEnabled} from './AboutBenchlingModal';

type UserMenuQueryTenant = UserMenuQuery['currentTenant'];

const MAX_GROUPS = 3;

export const USER_MENU_QUERY: DocumentNode = gql`
  query UserMenuQuery @skipAudit @engComponent(name: NAVIGATION) {
    currentTenant {
      id
      avatarUrl
      permissions {
        admin
        canCreateOrganizations
        canCreateUsers
      }
    }
    currentUser {
      ... on UserNode {
        id
      }
      ... on BenchlingAppNode {
        id
      }
      ... on ServiceAccountActorNode {
        id
      }
    }
  }
`;

interface UserMenuUser extends User {
  organizations: Array<Organization>;
  teams: Array<Team>;
}

export interface UserMenuProps {
  children: JSX.Element;
  currentTenant: UserMenuQueryTenant | null;
  currentUser: UserMenuUser;
  isOpen: boolean;
  registrySettingsURL: string | null;
  setIsOpen: (isOpen: boolean) => void;
}

class PresentationalUserMenu extends React.Component<UserMenuProps, {}> {
  _renderUserItem = (): JSX.Element => (
    <li className={css(userMenuStyles.userInfo)}>
      <Avatar
        entity={this.props.currentUser}
        className={css(userMenuStyles.userAvatar)}
        size={AvatarSize.LARGE}
      />
      <div className={css(userMenuStyles.userText)}>
        <div
          className={css(userMenuStyles.header)}
          {...getTestProps({component: 'UserMenu', element: 'userText'})}
        >
          {getPartyDisplayString(this.props.currentUser)}
        </div>
        <div className={classNames(css(userMenuStyles.subheader), '_test-userMenu-subheader')}>
          <a
            className={css(userMenuStyles.link)}
            href={`/${this.props.currentUser.url_identifier}/settings`}
            {...getTestProps({component: 'UserMenu', element: 'settings'})}
          >
            Settings
          </a>
        </div>
      </div>
    </li>
  );

  _renderTenantAdminConsoleItem(): JSX.Element {
    const avatarUrl = this.props.currentTenant?.avatarUrl ?? NO_AVATAR_URL;
    return (
      <Section>
        <Option
          avatar={
            <Avatar
              entity={{avatar_url: avatarUrl, name: 'Tenant admin console'}}
              size={AvatarSize.XSMALL}
              shape={AvatarShape.SQUARE}
            />
          }
          avatarClassName={css(userMenuStyles.menuItemAvatar)}
          linkClassName={css(userMenuStyles.menuItem)}
          dataTestAttrs={{component: 'TenantAdminConsole', element: 'item'}}
          href="/admin"
        >
          Tenant admin console
        </Option>
      </Section>
    );
  }

  _renderGroupMenuItem(
    group: Organization | Team,
    getHref: (group: Organization | Team) => string
  ): JSX.Element {
    const href = getHref(group);
    return (
      <Option
        avatar={
          <Avatar
            entity={{avatar_url: group.avatar_url, name: group.name}}
            size={AvatarSize.XSMALL}
            shape={AvatarShape.SQUARE}
          />
        }
        avatarClassName={css(userMenuStyles.menuItemAvatar)}
        key={group.id}
        href={href}
        linkClassName={css(userMenuStyles.menuItem)}
        onSelect={(_, e) => {
          BenchlingUtil.navigate(e!, href);
        }}
        dataTestAttrs={{component: 'GroupMenu', element: 'item'}}
      >
        {group.name}
      </Option>
    );
  }

  _renderGroupMenuItems(
    groups: Array<Organization | Team>,
    icon: BenchlingIconName,
    name: string,
    getHref: (group: Organization | Team) => string
  ): JSX.Element {
    if (groups.length > MAX_GROUPS) {
      const firstItemHref = getHref(groups[0]);
      return (
        <Option
          icon={icon}
          iconClassName={css(userMenuStyles.menuItemIcon)}
          iconWrapperClassName={css(userMenuStyles.menuItemIconWrapper)}
          linkClassName={css(userMenuStyles.menuItem)}
          onSelect={(_, e) => {
            BenchlingUtil.navigate(e!, firstItemHref);
          }}
          href={firstItemHref}
        >
          {name}
          <Menu dropUp={true} scrollable={true} height={MenuHeight.LARGE} width={MenuWidth.XLARGE}>
            {groups.map((group) => this._renderGroupMenuItem(group, getHref))}
          </Menu>
        </Option>
      );
    }
    return <Section>{groups.map((group) => this._renderGroupMenuItem(group, getHref))}</Section>;
  }

  _getFeatureSettingsSubmenuItems(): Array<Omit<OptionProps<string>, 'shouldEllipsize'>> {
    const maybeItems: Array<Omit<OptionProps<string>, 'shouldEllipsize'> | false> = [
      isFeatureEnabled({feature: RegistryFeature.REGISTRY_ADMIN}) &&
        this.props.registrySettingsURL != null && {
          children: 'Registry settings',
          icon: ModelTypeConstants.REGISTRY,
          onSelect: (_, e) => {
            BenchlingUtil.navigate(e as BasicEvent, this.props.registrySettingsURL!);
          },
          href: this.props.registrySettingsURL,
        },
      {
        children: capitalize(schemaFieldsetNamePluralized()),
        icon: ModelTypeConstants.SCHEMA_INTERFACE,
        onSelect: (_, e) => {
          BenchlingUtil.navigate(e as BasicEvent, schemaInterfacesURL());
        },
        href: schemaInterfacesURL(),
      },
      isFeatureEnabled({feature: InventoryFeature.INVENTORY_ADMIN}) && {
        children: 'Inventory settings',
        icon: ModelTypeConstants.LOCATION,
        onSelect: (_, e) => {
          BenchlingUtil.navigate(e as BasicEvent, getTenantLevelInventorySettingsURL());
        },
        href: getTenantLevelInventorySettingsURL(),
      },
      areMonomersEnabledForUser() && {
        children: 'Nucleotide monomer library',
        icon: 'monomer',
        onSelect: (_, e) => {
          BenchlingUtil.navigate(e as BasicEvent, MonomersDAO.MONOMER_LIBRARY_URL);
        },
        href: MonomersDAO.MONOMER_LIBRARY_URL,
      },
      areAaMonomersEnabledForUser() && {
        children: 'AA monomer library',
        icon: 'monomer',
        onSelect: (_, e) => {
          BenchlingUtil.navigate(e as BasicEvent, AA_MONOMER_LIBRARY_URL);
        },
        href: AA_MONOMER_LIBRARY_URL,
      },
      BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS && {
        children: 'Salt & Solvate library',
        icon: 'powder',
        onSelect: (_, e) => {
          BenchlingUtil.navigate(e as BasicEvent, ChemistryDAO.SALT_LIBRARY_URL);
        },
        href: ChemistryDAO.SALT_LIBRARY_URL,
      },
      {
        children: 'Unit dictionary',
        icon: 'unit',
        onSelect: (_, e) => {
          BenchlingUtil.navigate(e as BasicEvent, UnitDictionaryDAO.UNIT_DICTIONARY_URL);
        },
        href: UnitDictionaryDAO.UNIT_DICTIONARY_URL,
      },
      BenchlingConfig.PROCEDURES_ENABLE_PARAMETER_LIBRARY && {
        children: 'Parameter library',
        icon: 'parameters',
        onSelect: (_, e) => {
          BenchlingUtil.navigate(
            e as BasicEvent,
            ParameterLibraryDAO.libraryParameterDefinitionsListingURL()
          );
        },
        href: ParameterLibraryDAO.libraryParameterDefinitionsListingURL(),
      },
      {
        children: 'Template collections',
        icon: ModelTypeConstants.TEMPLATE_COLLECTION,
        onSelect: (_, e) => {
          BenchlingUtil.navigate(e as BasicEvent, TemplateCollectionDAO.collectionsURL());
        },
        href: TemplateCollectionDAO.collectionsURL(),
      },
      areReviewLifecyclesEnabledForUser() && {
        children: 'Review processes',
        icon: 'review_lifecycles',
        onSelect: (_, e) => {
          BenchlingUtil.navigate(e as BasicEvent, ReviewLifecyclesDAO.reviewLifecyclesListingURL());
        },
        href: ReviewLifecyclesDAO.reviewLifecyclesListingURL(),
      },
      areAnyFeaturesEnabled([{feature: LabAutomationFeature.LAB_AUTOMATION_ADMIN}]) && {
        children: 'Run schemas',
        icon: ModelTypeConstants.RUN_SCHEMA,
        onSelect: (_, e) => {
          BenchlingUtil.navigate(e as BasicEvent, AssaySchemaDAO.assaySchemasURL(ASSAY_TYPES.ASSAY_RUN));
        },
        href: AssaySchemaDAO.assaySchemasURL(ASSAY_TYPES.ASSAY_RUN),
      },
      isFeatureEnabled({feature: ResultsFeature.RESULTS_ADMIN}) && {
        children: RESULT_PRODUCT_SCHEMAS,
        icon: ModelTypeConstants.RESULT_SCHEMA,
        onSelect: (_, e) => {
          BenchlingUtil.navigate(e as BasicEvent, AssaySchemaDAO.assaySchemasURL(ASSAY_TYPES.ASSAY_RESULT));
        },
        href: AssaySchemaDAO.assaySchemasURL(ASSAY_TYPES.ASSAY_RESULT),
      },
      BenchlingConfig.ENABLE_REQUESTS && {
        children: `${getLegacyRequestObjectName()} schemas`,
        icon: ModelTypeConstants.REQUEST_SCHEMA,
        onSelect: (_, e) => {
          BenchlingUtil.navigate(e as BasicEvent, AssaySchemaDAO.assaySchemasURL(ASSAY_TYPES.ASSAY_REQUEST));
        },
        href: AssaySchemaDAO.assaySchemasURL(ASSAY_TYPES.ASSAY_REQUEST),
      },
      BenchlingConfig.STUDIES_ENABLE && {
        children: 'Study schemas',
        icon: ModelTypeConstants.STUDY,
        onSelect: (_, e) => {
          BenchlingUtil.navigate(e as BasicEvent, getStudySchemaListingURL());
        },
        href: getStudySchemaListingURL(),
      },
      BenchlingConfig.SAMPLE_TESTS_ENABLE && {
        children: 'Sample types',
        icon: 'sample',
        onSelect: (_, e) => {
          BenchlingUtil.navigate(e as BasicEvent, SAMPLE_TYPES_URL);
        },
        href: SAMPLE_TYPES_URL,
      },
      isFeatureEnabled({feature: ProcessesFeature.PROCESSES_ADMIN}) && {
        children: 'Workflow schemas',
        icon: 'process',
        onSelect: (_, e) => {
          BenchlingUtil.navigate(e as BasicEvent, ProcessSchemaDAO.processTaskSchemasURL());
        },
        href: ProcessSchemaDAO.processTaskSchemasURL(),
      },
      BenchlingConfig.SAMPLE_TESTS_ENABLE && {
        children: 'Test definitions',
        icon: 'protocol-table',
        onSelect: (_, e) => {
          BenchlingUtil.navigate(e as BasicEvent, TestDefinitionDAO.testDefinitionsListingURL());
        },
        href: TestDefinitionDAO.testDefinitionsListingURL(),
      },
      BenchlingConfig.ENABLE_POLICY_UI && {
        children: 'Access policies',
        icon: 'lock',
        onSelect: (_, e) => {
          BenchlingUtil.navigate(e as BasicEvent, PolicyDAO.getDataPoliciesURL());
        },
        href: PolicyDAO.getDataPoliciesURL(),
      },
      shouldShowDeveloperConsole(this.props.currentTenant?.permissions?.admin ?? false) && {
        children: 'Developer console',
        icon: 'console',
        onSelect: (_, e) => {
          const href = BenchlingConfig.ENABLE_BENCHLING_APPS
            ? BenchlingAppDAO.BENCHLING_APPS_WEB_URL
            : EventsDAO.EVENT_SUBSCRIPTIONS_URL;
          BenchlingUtil.navigate(e as BasicEvent, href);
        },
        href: BenchlingConfig.ENABLE_BENCHLING_APPS
          ? BenchlingAppDAO.BENCHLING_APPS_WEB_URL
          : EventsDAO.EVENT_SUBSCRIPTIONS_URL,
      },
      BenchlingConfig.MIGRATION_ENABLE_CONFIG_MIGRATION && {
        children: 'Configuration migration',
        icon: 'converted-from',
        onSelect: (_, e) => {
          BenchlingUtil.navigate(e as BasicEvent, ConfigMigrationDAO.getConfigMigrationURL());
        },
        href: ConfigMigrationDAO.getConfigMigrationURL(),
      },
    ];
    return maybeItems.filter((item): item is Omit<OptionProps<string>, 'shouldEllipsize'> => Boolean(item));
  }

  _getMolbioSettingsSubmenuItems(): Array<Omit<OptionProps<string>, 'shouldEllipsize'>> {
    return [
      {
        children: 'Enzyme lists',
        icon: 'enzyme',
        href: `/${this.props.currentUser.url_identifier}/enzyme-lists`,
      },
      {
        children: 'Feature libraries',
        icon: 'feature-libraries',
        href: `/${this.props.currentUser.url_identifier}/feature-libraries`,
      },
      {
        children: 'Ladders',
        icon: 'ladder',
        href: `/${this.props.currentUser.url_identifier}/ladders`,
      },
    ];
  }

  _getHelpSubmenuItems(): Array<Omit<OptionProps<string>, 'shouldEllipsize'>> {
    const maybeItems: Array<Omit<OptionProps<string>, 'shouldEllipsize'> | false> = [
      isAboutBenchlingModalEnabled() && {
        children: 'About Benchling',
        icon: 'benchling',
        onSelect: () => {
          legacyRunModal(AboutBenchlingModal, {});
        },
      },
      {
        children: 'Help center',
        icon: 'more-info',
        ...(BenchlingConfig.ENABLE_ZENDESK_GUIDE
          ? {
              href: ZENDESK_GUIDE_URL,
            }
          : {href: BenchlingConfig.SUPPORT_SITE_URL}),
      },
      {
        children: 'Ask the community',
        icon: 'team',
        href: 'https://community.benchling.com/',
      },
    ];
    return maybeItems.filter((item): item is Omit<OptionProps<string>, 'shouldEllipsize'> => Boolean(item));
  }

  override render(): JSX.Element {
    const {currentTenant} = this.props;

    const featureSettingsSubmenuItems = this._getFeatureSettingsSubmenuItems();
    const molbioSettingsSubmenuItems = this._getMolbioSettingsSubmenuItems();
    const helpSubmenuItems = this._getHelpSubmenuItems();
    // The user section is wrapped in a form tag to prevent clicking from dismissing the menu.
    const userItem = (
      <form className={css(userMenuStyles.container, userMenuStyles.topBorderRadii)}>
        {this._renderUserItem()}
      </form>
    );
    return (
      <Dropdown
        closeOnInsideClick={true}
        closeOnOutsideClick={true}
        isOpen={this.props.isOpen}
        onToggle={() => {
          trackEvent({
            module: ANALYTICS_MODULE.NAVIGATION,
            actionCategory: ACTION_CATEGORY.READ,
            action: 'clicked_user_icon',
          });
          this.props.setIsOpen(!this.props.isOpen);
        }}
        menu={
          <Menu
            className={css(userMenuStyles.menu)}
            width={MenuWidth.XLARGE}
            dataTestAttrs={{component: 'UserMenu', element: 'dropdownMenu'}}
          >
            <Section>
              <Option
                contentClassName={css(userMenuStyles.userItem)}
                contentType={OptionContentType.PLAIN}
                childrenClassName={css(userMenuStyles.userItem)}
                linkClassName={css(userMenuStyles.focusReset)}
                disableEventPropagation={true}
                onSelect={noop}
                optionNameClassName={css(userMenuStyles.topBorderRadii)}
              >
                {userItem}
              </Option>
            </Section>
            {BenchlingConfig.ENABLE_TENANT_ADMIN_CONSOLE &&
              ((currentTenant?.permissions?.admin || currentTenant?.permissions?.canCreateUsers) ?? false) &&
              this._renderTenantAdminConsoleItem()}
            {this._renderGroupMenuItems(
              this.props.currentUser.organizations,
              ModelTypeConstants.ORGANIZATION,
              'Organizations',
              (organization: Organization) => OrganizationDAO.organizationURL(organization.url_identifier)
            )}
            {this._renderGroupMenuItems(
              this.props.currentUser.teams,
              ModelTypeConstants.TEAM,
              'Teams',
              (team: Team) => TeamDAO.teamURL(team.id)
            )}
            <Section>
              <Option
                icon="settings"
                iconClassName={css(userMenuStyles.menuItemIcon)}
                iconWrapperClassName={css(userMenuStyles.menuItemIconWrapper)}
                href={featureSettingsSubmenuItems[0].href}
                onSelect={featureSettingsSubmenuItems[0].onSelect}
                linkClassName={css(userMenuStyles.menuItem)}
                dataTestAttrs={{component: 'FeatureSettings', element: 'item'}}
              >
                Feature settings
                <Menu dropUp={true}>
                  {featureSettingsSubmenuItems.map(({children, ...restProps}, index) => (
                    <Option
                      iconWrapperClassName={css(userMenuStyles.menuItemIconWrapper)}
                      linkClassName={css(userMenuStyles.menuItem)}
                      key={`featureSettings-${index}`}
                      {...restProps}
                    >
                      {children}
                    </Option>
                  ))}
                </Menu>
              </Option>
              {isFeatureEnabled({feature: MolbioFeature.MOLBIO_ADMIN}) && (
                <Option
                  icon="wrench"
                  iconClassName={css(userMenuStyles.menuItemIcon)}
                  iconWrapperClassName={css(userMenuStyles.menuItemIconWrapper)}
                  href={molbioSettingsSubmenuItems[0].href}
                  onSelect={molbioSettingsSubmenuItems[0].onSelect}
                  linkClassName={css(userMenuStyles.menuItem)}
                >
                  Molecular biology settings
                  <Menu dropUp={true}>
                    {molbioSettingsSubmenuItems.map(({children, ...restProps}, index) => (
                      <Option {...restProps} key={`molbioSettings-${index}`}>
                        {children}
                      </Option>
                    ))}
                  </Menu>
                </Option>
              )}
              {BenchlingConfig.ENABLE_EXPORTS && (
                <Option
                  icon="export"
                  iconClassName={css(userMenuStyles.menuItemIcon)}
                  iconWrapperClassName={css(userMenuStyles.menuItemIconWrapper)}
                  linkClassName={css(userMenuStyles.menuItem)}
                  onSelect={() => {
                    ExportModal.open();
                  }}
                >
                  Data export
                </Option>
              )}
            </Section>
            <Section>
              <Option
                icon="more-info"
                iconClassName={css(userMenuStyles.menuItemIcon)}
                iconWrapperClassName={css(userMenuStyles.menuItemIconWrapper)}
                linkClassName={css(userMenuStyles.menuItem)}
                {...(BenchlingConfig.ENABLE_ZENDESK_GUIDE
                  ? {
                      onSelect: (_, e) => {
                        BenchlingUtil.navigate(e as BasicEvent, ZENDESK_GUIDE_URL);
                      },
                      href: ZENDESK_GUIDE_URL,
                    }
                  : {href: BenchlingConfig.SUPPORT_SITE_URL})}
              >
                Help
                <Menu>
                  {helpSubmenuItems.map(({children, ...restProps}, index) => (
                    <Option {...restProps} key={`help-${index}`}>
                      {children}
                    </Option>
                  ))}
                </Menu>
              </Option>
              {BenchlingConfig.ENABLE_GLOBAL_INVITE_SCIENTISTS && (
                <Option
                  icon="invite"
                  iconClassName={css(userMenuStyles.menuItemIcon)}
                  iconWrapperClassName={css(userMenuStyles.menuItemIconWrapper)}
                  linkClassName={css(userMenuStyles.menuItem)}
                  href="/invite"
                >
                  Invite scientists
                </Option>
              )}
            </Section>
            <Section>
              {UpgradeAccountModal.isEnabled(this.props.currentUser) && (
                <Option
                  icon="star"
                  iconClassName={css(userMenuStyles.menuItemIcon, userMenuStyles.upgradeIcon)}
                  iconWrapperClassName={css(userMenuStyles.menuItemIconWrapper)}
                  linkClassName={css(userMenuStyles.menuItem)}
                  onSelect={() => {
                    // @ts-expect-error need to add initialTab here, or make optional
                    UpgradeAccountModal.open();
                  }}
                >
                  Upgrade account
                </Option>
              )}
              {(!BenchlingConfig.PRIVATE_DEPLOY ||
                (currentTenant?.permissions?.canCreateOrganizations ?? false)) && (
                <Option
                  className="legacy-font-proxima-title js-createOrganizationButton"
                  icon="compose"
                  iconClassName={css(userMenuStyles.menuItemIcon, userMenuStyles.createOrganizationsIcon)}
                  iconWrapperClassName={css(userMenuStyles.menuItemIconWrapper)}
                  linkClassName={css(userMenuStyles.menuItem)}
                  onSelect={(_, e) => {
                    // If the user is ctrl-clicking, open it in a new tab.
                    if (!BenchlingUtil.isSimpleClick(e!)) {
                      window.open('/organizations/create');
                      return;
                    }
                    e!.preventDefault();
                    HomeDashboardActions.openSearchOrgModal();
                  }}
                >
                  Create or join organization
                </Option>
              )}
              <Option
                icon="sign-out"
                iconClassName={css(userMenuStyles.menuItemIcon, userMenuStyles.signOutIcon)}
                iconWrapperClassName={css(userMenuStyles.menuItemIconWrapper)}
                linkClassName={css(userMenuStyles.menuItem)}
                href="/signout"
                className="legacy-font-proxima-title"
                onSelect={(_value, e) => {
                  e?.preventDefault();
                  signOut();
                }}
              >
                Sign out
              </Option>
            </Section>
          </Menu>
        }
      >
        {this.props.children}
      </Dropdown>
    );
  }
}

function getRegistrySettingsURL(sources: Array<NavStoreSourceGraphQL>): string | null {
  const registry = sources.flatMap((source) => (source.__typename === 'RegistryNode' ? [source] : []))[0];
  return registry
    ? RegistryUtil.getRegistrySettingsUrl(registry.ownerJson.url_identifier, registry.id)
    : null;
}

export default function UserMenu(
  props: Omit<UserMenuProps, 'registrySettingsURL' | 'currentTenant'>
): JSX.Element {
  // TODO(GraphQL): This will be cleaner once we upgrade to Apollo Client 3 and get hooks. (As of writing,
  // they're still in beta).
  const innerProps = {...props, registrySettingsURL: null, currentTenant: null};
  return (
    <ErrorHandlingQuery<UserMenuQuery> query={USER_MENU_QUERY}>
      {({data: userMenuData, loading: userMenuQueryLoading}) => (
        <ErrorHandlingQuery<AllSourcesQuery> query={ALL_SOURCES_QUERY}>
          {({data: sourcesData, loading: sourcesLoading}) => (
            <PresentationalUserMenu
              {...innerProps}
              currentTenant={userMenuData?.currentTenant ?? null}
              registrySettingsURL={sourcesLoading ? null : getRegistrySettingsURL(sourcesData!.sources)}
            />
          )}
        </ErrorHandlingQuery>
      )}
    </ErrorHandlingQuery>
  );
}

const linkHoverStyles = {
  color: Colors.GRAY_LIGHT,
  textDecoration: 'none',
};
export const userMenuStyles = StyleSheet.create({
  menu: {
    bottom: 0,
    left: '100%',
    position: 'absolute',
    top: 'auto',
  },
  userItem: {
    padding: 0,
  },
  focusReset: {
    outline: 'none',
  },
  container: {
    borderRadius: 'inherit',
  },
  menuItem: {
    minHeight: '42px',
  },
  topBorderRadii: {
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
  },
  menuItemAvatar: {
    height: '42px',
    width: '42px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '-16px',
  },
  menuItemIconWrapper: {
    height: '42px',
    width: '42px',
  },
  menuItemIcon: {
    height: '14px',
    width: '14px',
  },
  upgradeIcon: {
    color: Color.GREEN_400,
  },
  createOrganizationsIcon: {
    color: Color.BLUE_500,
  },
  signOutIcon: {
    color: Color.RED_400,
  },
  userText: {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
  },
  userInfo: {
    backgroundColor: Colors.BLUE_DARKER,
    borderRadius: 'inherit',
    color: Colors.WHITE,
    cursor: 'default',
    display: 'flex',
    padding: PADDING_SIDEPANEL,
  },
  header: {
    ...ellipsisText,
    fontSize: 16,
    lineHeight: '24px',
    marginBottom: 6,
  },
  subheader: {
    fontSize: 12,
    lineHeight: '16px',
    marginBottom: 3,
  },
  link: {
    color: Colors.WHITE,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    ':hover': linkHoverStyles,
    ':focus': linkHoverStyles,
    ':active': {
      color: Colors.GRAY,
      textDecoration: 'none',
    },
  },
  userAvatar: {
    marginRight: 14,
  },
});
